/**
 * `@captello/ulc-webview-sdk/mobile-host` — for an **application embedded inside the
 * Captello mobile app** (the meeting platform, Connexions, …).
 *
 * The mobile app hosts the application in an iframe and offers it native services over
 * `postMessage`: an auth token, the badge scanner, back navigation, sharing.
 * {@link MobileHostClient} is the embedded application's client for that channel; the
 * mobile app's own side is `EmbeddedAppClient` (`@captello/ulc-webview-sdk/embedded-app`).
 *
 * Not to be confused with `CaptelloWebview`, which is for a page that embeds the
 * capture webview — there your page is the host. See {@link MobileHostRequestType} for
 * the wire format, which also differs (plain objects, SCREAMING_CASE).
 *
 * @example
 * import { MobileHostClient } from "@captello/ulc-webview-sdk/mobile-host";
 *
 * const mobileHost = new MobileHostClient();
 * const token = await mobileHost.requestAuthToken();
 * mobileHost.notifyReady();
 * const [person] = await mobileHost.openScanner();
 */

import type { Unsubscribe } from "./client";
import {
    makeSubscription,
    MobileHostRequestType,
    MobileHostResponseType,
    parseMobileHostResponse,
} from "./mobile-app-protocol";
import type { MobileHostRequest, MobileHostResponseMap, ScannedPerson } from "./mobile-app-protocol";

export {
    MobileHostRequestType,
    MobileHostResponseType,
    parseMobileHostRequest,
    parseMobileHostResponse,
} from "./mobile-app-protocol";
export type {
    MobileHostRequest,
    MobileHostRequestMap,
    MobileHostResponse,
    MobileHostResponseMap,
    AuthTokenMessage,
    ScannerResultMessage,
    ScannerClosedMessage,
    ScannedPerson,
    MeetingScheduledRequest,
} from "./mobile-app-protocol";

/** Rejection reason from {@link MobileHostClient.openScanner} when the app reports a failure. */
export class ScannerError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ScannerError";
    }
}

/** Listener for a specific mobile-app response type. */
export type MobileHostResponseListener<T extends MobileHostResponseType> = (message: MobileHostResponseMap[T]) => void;

export interface MobileHostClientOptions {
    /** Window the mobile app is listening on. Defaults to `window.parent`. */
    mobileHostWindow?: Window;
    /** Window to receive the mobile app's responses on. Defaults to `window`. */
    embeddedWindow?: Window;
    /**
     * `targetOrigin` for outgoing `postMessage` calls. Defaults to `"*"`: the mobile app's
     * webview origin differs per platform (`capacitor://localhost`, `http://localhost`),
     * and an application only uses this channel when it is running inside the app.
     */
    targetOrigin?: string;
}

/**
 * Embedded-application-side client for the Captello mobile app that hosts it.
 *
 * Construct one per page and keep it for the page's lifetime. It attaches a single
 * `message` listener on construction; call {@link destroy} to remove it.
 */
export class MobileHostClient {
    private readonly mobileHostWindow: Window;
    private readonly embeddedWindow: Window;
    private readonly targetOrigin: string;
    private readonly listeners = new Map<
        MobileHostResponseType,
        Set<MobileHostResponseListener<MobileHostResponseType>>
    >();
    private readonly boundHandler = (event: MessageEvent) => this.handleMessage(event);
    private destroyed = false;

    constructor(options: MobileHostClientOptions = {}) {
        const embeddedWindow = options.embeddedWindow ?? (typeof window !== "undefined" ? window : undefined);
        if (!embeddedWindow) {
            throw new Error(
                "MobileHostClient: no window available. Pass `embeddedWindow` when constructing outside a browser.",
            );
        }
        this.embeddedWindow = embeddedWindow;
        this.mobileHostWindow = options.mobileHostWindow ?? embeddedWindow.parent;
        this.targetOrigin = options.targetOrigin ?? "*";
        this.embeddedWindow.addEventListener("message", this.boundHandler);
    }

    /** Post a request to the mobile app. Prefer the typed methods; this is the escape hatch. */
    send(request: MobileHostRequest): void {
        if (this.destroyed) return;
        this.mobileHostWindow.postMessage(request, this.targetOrigin);
    }

    /** Subscribe to a response type. Returns a handle with `unsubscribe()` (also callable). */
    on<T extends MobileHostResponseType>(type: T, listener: MobileHostResponseListener<T>): Unsubscribe {
        let set = this.listeners.get(type);
        if (!set) {
            set = new Set();
            this.listeners.set(type, set);
        }
        set.add(listener as MobileHostResponseListener<MobileHostResponseType>);
        return makeSubscription(() => {
            set?.delete(listener as MobileHostResponseListener<MobileHostResponseType>);
        });
    }

    /** Tell the mobile app the application is ready; it hides its loading spinner. */
    notifyReady(): void {
        this.send({ type: MobileHostRequestType.AppReady });
    }

    /** Show a user-facing error in the mobile app. */
    notifyError(message: string): void {
        this.send({ type: MobileHostRequestType.Error, message });
    }

    /** Ask the mobile app to close the application. */
    navigateBack(): void {
        this.send({ type: MobileHostRequestType.NavigateBack });
    }

    /**
     * Tell the mobile app a meeting was booked. The app takes the rep back to the screen
     * that opened the application; an app build that predates this message ignores it.
     */
    notifyMeetingScheduled(meetingId: number): void {
        this.send({ type: MobileHostRequestType.MeetingScheduled, meetingId });
    }

    /** Resolves with the magic token the mobile app mints for the current user. */
    requestAuthToken(): Promise<string> {
        return new Promise((resolve) => {
            const off = this.on(MobileHostResponseType.AuthToken, (message) => {
                off();
                resolve(message.token);
            });
            this.send({ type: MobileHostRequestType.RequestAuthToken });
        });
    }

    /**
     * Opens the mobile app's badge scanner and resolves once the session ends with every
     * person it captured, in scan order — several for a group scan, none if the user
     * cancelled. Rejects with a {@link ScannerError} if the app reports a failure.
     *
     * A badge whose lookup returned nothing is still included, with its `badgeId` and
     * empty `fields`, so it can be linked to the attendee.
     */
    openScanner(): Promise<ScannedPerson[]> {
        return new Promise((resolve, reject) => {
            const people: ScannedPerson[] = [];
            const done = () => {
                offResult();
                offClosed();
            };
            const offResult = this.on(MobileHostResponseType.ScannerResult, (message) => {
                if (!Array.isArray(message.result)) {
                    done();
                    reject(new ScannerError(message.message || "Scan failed."));
                    return;
                }
                people.push({
                    badgeId: typeof message.badgeId === "string" ? message.badgeId : "",
                    fields: message.result,
                });
                // App builds that predate multi-person sessions post one result, without a
                // badgeId, and never a ScannerClosed — settle on it.
                if (!("badgeId" in message)) {
                    done();
                    resolve(people);
                }
            });
            const offClosed = this.on(MobileHostResponseType.ScannerClosed, () => {
                done();
                resolve(people);
            });
            this.send({ type: MobileHostRequestType.OpenScanner });
        });
    }

    /** Remove the `message` listener and drop every subscription. Safe to call more than once. */
    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.embeddedWindow.removeEventListener("message", this.boundHandler);
        this.listeners.clear();
    }

    private handleMessage(event: MessageEvent): void {
        const message = parseMobileHostResponse(event.data);
        if (!message) return;
        const set = this.listeners.get(message.type);
        if (!set) return;
        for (const listener of Array.from(set)) {
            listener(message);
        }
    }
}
